/**
 * Strength Reports Domain
 * Stores quarterly strength summaries for reporting UI, and reads them: a
 * team's strength close by close, each team's newest, and a score as the
 * five-step rating every screen shows it as.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { MessageType, StrengthReport } from '../constants/op-codes';
import { StrengthMax, StrengthMin } from '../constants/server-finance';

/** The rating's words, weakest first, as translation keys. */
const STRENGTH_WORD_KEYS = [
  'hq.strength.weak',
  'hq.strength.modest',
  'hq.strength.steady',
  'hq.strength.strong',
  'hq.strength.dominant',
] as const;

interface StrengthRating {
  /** Filled segments of five. */
  steps: 1 | 2 | 3 | 4 | 5;
  /** The step in a word, as a translation key: "Weak" … "Dominant". */
  wordKey: (typeof STRENGTH_WORD_KEYS)[number];
}

/**
 * A strength score as one of five steps: the server's range, 0.25 to 2.0,
 * cut into five equal bands, so the baseline of 1.0 — a team that margin and
 * leverage have not moved — reads as the middle step.
 */
export function strengthRating(strength: number): StrengthRating {
  const share = (Math.min(StrengthMax, Math.max(StrengthMin, strength)) - StrengthMin) / (StrengthMax - StrengthMin);
  const index = Math.min(4, Math.floor(share * 5));
  return { steps: (index + 1) as StrengthRating['steps'], wordKey: STRENGTH_WORD_KEYS[index] };
}

/** The strength reports as the server sends them (reports.go): the router's guard returns this. */
export interface StrengthReportMessage {
  type: typeof MessageType.MessageTypeStrengthReport;
  quarter_id: number;
  reports: { [team_id: number]: StrengthReport };
}

interface StrengthReportsState {
  strengthReports: { [quarter_id: number]: { [team_id: number]: StrengthReport } };
  addStrengthReports: (quarterId: number, reports: { [team_id: number]: StrengthReport }) => void;
  reset: () => void;
}

const initialState = {
  strengthReports: {},
};

export const useStrengthReportsStore = create<StrengthReportsState>()(
  devtools(
    (set) => ({
      ...initialState,

      addStrengthReports: (quarterId, reports) => {
        set(
          (state) => ({
            strengthReports: {
              ...state.strengthReports,
              // Merged, not replaced: the server sends each team's report on its own.
              [quarterId]: { ...state.strengthReports[quarterId], ...reports },
            },
          }),
          false,
          'addStrengthReports'
        );
      },

      reset: () => {
        set(initialState, false, 'resetStrengthReports');
      },
    }),
    { name: 'strength-reports-store' }
  )
);

// ==================== READERS ====================

/** Strength reports, kept by quarter, then by team. */
type StrengthReports = Record<number, Record<number, { strength: number }>>;

/** A team's strength at one quarter's close. */
export interface StrengthPoint {
  quarter: number;
  strength: number;
}

/** A team's strength at each close it has had, oldest first. */
export function strengthHistory(reports: StrengthReports, teamId: number | undefined): StrengthPoint[] {
  if (teamId === undefined) return [];
  return Object.keys(reports)
    .map(Number)
    .sort((a, b) => a - b)
    .flatMap((quarter) => {
      const report = reports[quarter]?.[teamId];
      return report ? [{ quarter, strength: report.strength }] : [];
    });
}

/** Reactive strengthHistory. */
export function useStrengthHistory(teamId: number | undefined): StrengthPoint[] {
  const reports = useStrengthReportsStore((s) => s.strengthReports);
  return useMemo(() => strengthHistory(reports, teamId), [reports, teamId]);
}

/**
 * The newest strength the server sent for each team. Each team keeps its own
 * quarter count, so one team's latest report can carry an older quarter
 * number than another's.
 */
export function latestStrengthByTeam(reports: StrengthReports): Record<number, number> {
  const latest: Record<number, number> = {};
  const quarters = Object.keys(reports)
    .map(Number)
    .sort((a, b) => b - a);
  for (const quarter of quarters) {
    for (const [teamId, report] of Object.entries(reports[quarter] ?? {})) {
      if (latest[Number(teamId)] === undefined && report) latest[Number(teamId)] = report.strength;
    }
  }
  return latest;
}

// ==================== HANDLERS ====================

export function handleStrengthReport(data: StrengthReportMessage) {
  console.log('[StrengthReports] Received strength report:', data);
  useStrengthReportsStore.getState().addStrengthReports(data.quarter_id, data.reports);
}
